import { App } from "antd";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

/**
 * Unified notification hook following Ant Design v5 best practices.
 * Uses App.useApp() to ensure ConfigProvider context is respected.
 *
 * @example
 * const { showSuccess, showError, notifyError } = useNotification();
 *
 * // Simple success
 * showSuccess("Item created!");
 *
 * // Simple error
 * showError("Failed to save");
 *
 * // API error with parsing
 * notifyError("Failed to fetch data", apiErrorResponse);
 */
export const useNotification = () => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  /**
   * Extract error message from API response
   */
  const extractErrorMessage = (err: any): string => {
    try {
      return JSON.parse(err)["message"];
    } catch (e) {
      return t("error_occurred");
    }
  };

  /**
   * Show success notification
   */
  const showSuccess = (description: string, message = t("success")) => {
    notification.success({
      message,
      description,
    });
  };

  /**
   * Show error notification
   */
  const showError = (description: string, message = t("error")) => {
    notification.error({
      message,
      description,
    });
  };

  /**
   * Show error notification with API error parsing
   */
  const notifyError = (title: string, err: any) => {
    notification.error({
      message: title,
      description: extractErrorMessage(err),
    });
  };

  /**
   * Show success notification for delete operations
   */
  const deleteSuccess = (itemName: string) => {
    notification.success({
      message: t("success"),
      description:
        t("deleted_successfully", { item: itemName }) || `Deleted ${itemName} successfully!`,
    });
  };

  /**
   * Show success notification for save/create operations
   */
  const saveSuccess = (itemName?: string) => {
    notification.success({
      message: t("success"),
      description: itemName
        ? t("saved_successfully", { item: itemName }) || `Saved ${itemName} successfully!`
        : t("saved_successfully_generic") || "Saved successfully!",
    });
  };

  /**
   * Show success notification for create operations
   */
  const createSuccess = (itemName?: string) => {
    notification.success({
      message: t("success"),
      description: itemName
        ? t("created_successfully", { item: itemName }) || `Created ${itemName} successfully!`
        : t("created_successfully_generic") || "Created successfully!",
    });
  };

  /**
   * Show success notification for update operations
   */
  const updateSuccess = (itemName?: string) => {
    notification.success({
      message: t("success"),
      description: itemName
        ? t("updated_successfully", { item: itemName }) || `Updated ${itemName} successfully!`
        : t("updated_successfully_generic") || "Updated successfully!",
    });
  };

  /**
   * Show warning notification
   */
  const showWarning = (description: string, message = t("warning")) => {
    notification.warning({
      message,
      description,
    });
  };

  /**
   * Show info notification
   */
  const showInfo = (description: string, message = t("info")) => {
    notification.info({
      message,
      description,
    });
  };

  /**
   * Show generic error (deprecated, use showError instead)
   */
  const genericError = (message = t("error")) => {
    notification.error({
      message,
      description: t("error_occurred"),
    });
  };

  return {
    showSuccess,
    showError,
    notifyError,
    deleteSuccess,
    saveSuccess,
    createSuccess,
    updateSuccess,
    showWarning,
    showInfo,
    genericError,
  };
};
